import { useEffect, useMemo, useRef, useState } from "react"
import { Alert, Box, Heading, Spinner, Text, VStack } from "@chakra-ui/react"
import { useParams } from "react-router"
import PublicContentService from "services/content"
import useLandingProductContent from "../../hooks/useLandingProductContent"
import ProductList from "components/product/list"

const contentService = new PublicContentService()
const TAILWIND_V42_BROWSER_SCRIPT = "https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4.2.0/dist/index.global.js"
const TAILWIND_V42_THEME = "@theme { --breakpoint-sm: 40rem; }"
const TAILWIND_THEME_STYLE_ID = "tw-v42-blog-landing-theme"
const TAILWIND_RUNTIME_SCRIPT_ID = "tw-v42-browser-runtime"

function ensureTailwindV42Ready() {
  if (typeof document === "undefined") {
    return
  }

  const head = document.head || document.getElementsByTagName("head")?.[0]
  if (!head) {
    return
  }

  let themeStyle = document.getElementById(TAILWIND_THEME_STYLE_ID)
  if (!themeStyle) {
    themeStyle = document.createElement("style")
    themeStyle.id = TAILWIND_THEME_STYLE_ID
    themeStyle.setAttribute("type", "text/tailwindcss")
    themeStyle.textContent = TAILWIND_V42_THEME
    head.appendChild(themeStyle)
  }

  if (!document.getElementById(TAILWIND_RUNTIME_SCRIPT_ID)) {
    const runtimeScript = document.createElement("script")
    runtimeScript.id = TAILWIND_RUNTIME_SCRIPT_ID
    runtimeScript.src = TAILWIND_V42_BROWSER_SCRIPT
    runtimeScript.async = true
    head.appendChild(runtimeScript)
  }
}

function buildPageTitle(post) {
  return String(post?.seo?.meta_title || post?.seo?.og_title || post?.title || "Bài viết")
}

function buildDescription(post) {
  return String(post?.seo?.meta_description || post?.seo?.og_description || post?.excerpt || "")
}

const BlogLanding = () => {
  const { slug } = useParams()
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [post, setPost] = useState(null)
  const htmlHostRef = useRef(null)

  const contentHtml = useMemo(() => String(post?.content_html || ""), [post])
  const landingContent = useLandingProductContent({
    contentHtml,
    productIDs: Array.isArray(post?.product_ids) ? post.product_ids : [],
  })
  const renderedHtml = String(landingContent?.html || "")
  const endProducts = Array.isArray(landingContent?.products) ? landingContent.products : []

  useEffect(() => {
    ensureTailwindV42Ready()

    const host = htmlHostRef.current
    if (!host) {
      return
    }

    host.innerHTML = [
      '<style>',
      '.blog-landing-content .landing-html-root { max-width: 100%; color: #0f172a; line-height: 1.65; }',
      '.blog-landing-content .landing-html-root img { display: block; max-width: 100%; height: auto; object-fit: cover; border-radius: 12px; }',
      '.blog-landing-content .landing-html-root table { width: 100%; max-width: 100%; overflow: auto; display: block; }',
      '.blog-landing-content .landing-html-root .lp-product-list-grid { display: grid; grid-template-columns: repeat(1, minmax(0, 1fr)); gap: 16px; }',
      '.blog-landing-content .landing-html-root .lp-product-card { border: 1px solid #e2e8f0; border-radius: 16px; padding: 12px; background: #fff; }',
      '.blog-landing-content .landing-html-root .lp-media-wrap { width: 100%; aspect-ratio: 4/3; overflow: hidden; border-radius: 12px; background: #f1f5f9; }',
      '.blog-landing-content .landing-html-root .lp-empty-media { display: flex; width: 100%; height: 100%; align-items: center; justify-content: center; color: #64748b; font-size: 13px; }',
      '.blog-landing-content .landing-html-root .lp-product-name { margin-top: 12px; font-size: 16px; font-weight: 700; line-height: 1.4; }',
      '.blog-landing-content .landing-html-root .lp-product-price { margin-top: 6px; font-size: 14px; font-weight: 700; color: #0f172a; }',
      '@media (min-width: 40rem) { .blog-landing-content .landing-html-root .lp-product-list-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }',
      '@media (min-width: 64rem) { .blog-landing-content .landing-html-root .lp-product-list-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }',
      '</style>',
      `<div class="landing-html-root">${renderedHtml}</div>`,
    ].join("")
  }, [renderedHtml])

  useEffect(() => {
    let mounted = true

    const run = async () => {
      setLoading(true)
      setErrorMessage("")

      try {
        const data = await contentService.getPostBySlug(slug)
        if (!mounted) {
          return
        }

        setPost(data || null)
        document.title = buildPageTitle(data)
      } catch (error) {
        if (!mounted) {
          return
        }

        setPost(null)
        setErrorMessage(error?.message || "Không thể tải bài viết")
      }

      if (mounted) {
        setLoading(false)
      }
    }

    run()

    return () => {
      mounted = false
    }
  }, [slug])

  if (loading) {
    return (
      <Box py={8}>
        <VStack gap={3}>
          <Spinner />
          <Text>Đang tải landing page...</Text>
        </VStack>
      </Box>
    )
  }

  if (errorMessage) {
    return (
      <Box py={6}>
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Không thể tải landing page</Alert.Title>
            <Alert.Description>{errorMessage}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      </Box>
    )
  }

  return (
    <Box py={3}>
      <VStack align="stretch" gap={4}>
        <Heading size="lg">{buildPageTitle(post)}</Heading>
        {!!buildDescription(post) && (
          <Text color="fg.muted">{buildDescription(post)}</Text>
        )}
        <div className="mx-auto max-w-sm w-full">
          <div ref={htmlHostRef} />
        </div>
        {endProducts.length > 0 && (
          <Box>
            <Heading size="md" mb={3}>Sản phẩm liên quan</Heading>
            <ProductList data={endProducts} />
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export default BlogLanding
